import { ThemeEvents } from '@theme/events';

const UI_TIER_CAP = 4;
const METAFIELD_TIER_CAP = Number.MAX_SAFE_INTEGER;
const CAMERA_QTY_MOBILE_MQ = window.matchMedia('(max-width: 768px)');

/**
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function usesMetafieldTiers(root) {
  return root.getAttribute('data-use-metafield-tiers') === 'true';
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    const format = window.theme?.moneyFormat || window.Shopify?.money_format;
    return format ? Shopify.formatMoney(cents, format) : Shopify.formatMoney(cents);
  }
  const amount = (cents / 100).toFixed(2);
  return `Rs. ${amount.replace(/\.00$/, '')}`;
}

/**
 * Per-unit savings from compare at vs selling price (cents).
 *
 * @param {number} unitPriceCents
 * @param {number} compareAtCents
 * @returns {number}
 */
function getUnitSaveCents(unitPriceCents, compareAtCents) {
  if (!Number.isFinite(unitPriceCents) || !Number.isFinite(compareAtCents)) return 0;
  if (compareAtCents <= unitPriceCents) return 0;
  return compareAtCents - unitPriceCents;
}

/**
 * Resolve oversell policy. Variant JSON wins; DOM (Liquid SSR) is fallback.
 * Tracked inventory with unknown policy defaults to deny (Shopify default).
 *
 * @param {Record<string, unknown> | null | undefined} variant
 * @param {HTMLElement | null | undefined} root
 * @returns {'continue' | 'deny' | null}
 */
function resolveInventoryPolicy(variant, root) {
  const variantPolicy = variant?.inventory_policy;
  if (variantPolicy === 'continue' || variantPolicy === 'deny') {
    return variantPolicy;
  }

  const domPolicy = root?.getAttribute('data-inventory-policy');
  if (domPolicy === 'continue' || domPolicy === 'deny') {
    return domPolicy;
  }

  if (root?.getAttribute('data-inventory-continue') === 'true') {
    return 'continue';
  }

  const managed =
    variant?.inventory_management === 'shopify' || root?.getAttribute('data-inventory-managed') === 'true';
  if (managed) {
    return 'deny';
  }

  return null;
}

/**
 * @param {HTMLElement | null | undefined} root
 * @param {Record<string, unknown> | null | undefined} variant
 * @returns {{ managed: boolean; inventoryQty: number }}
 */
function getInventorySnapshot(root, variant) {
  const managed =
    variant?.inventory_management === 'shopify' || root?.getAttribute('data-inventory-managed') === 'true';

  let inventoryQty = Number(variant?.inventory_quantity);
  if (!Number.isFinite(inventoryQty) && root) {
    inventoryQty = Number(root.getAttribute('data-inventory-quantity'));
  }

  return { managed, inventoryQty };
}

/**
 * Inventory units for deny-oversell variants (null = no inventory cap applies).
 * Cart quantity is not subtracted — items in cart are not purchased yet.
 *
 * @param {Record<string, unknown> | null | undefined} variant
 * @param {HTMLElement} root
 * @returns {number | null}
 */
function getAvailableInventory(variant, root) {
  const policy = resolveInventoryPolicy(variant, root);
  const { managed, inventoryQty } = getInventorySnapshot(root, variant);

  if (!managed || policy !== 'deny') {
    return null;
  }

  if (Number.isFinite(inventoryQty)) {
    return Math.max(0, inventoryQty);
  }

  const rawQty = root.getAttribute('data-inventory-quantity');
  if (rawQty == null || rawQty === '') {
    return null;
  }

  const domQty = Number(rawQty);
  if (!Number.isFinite(domQty)) {
    return null;
  }

  return Math.max(0, domQty);
}

/**
 * Max selectable tier (0–4). Mirrors qubo-camera-quantity-max-tier.liquid.
 *
 * @param {Record<string, unknown> | null | undefined} variant
 * @param {HTMLElement} root
 * @returns {number}
 */
function calculateMaxTier(variant, root) {
  let max = usesMetafieldTiers(root) ? METAFIELD_TIER_CAP : UI_TIER_CAP;
  const quantityRule = /** @type {{ max?: number | null; min?: number }} | undefined */ (variant?.quantity_rule);

  if (quantityRule?.max != null && Number.isFinite(quantityRule.max)) {
    max = Math.min(max, quantityRule.max);
  }

  const available = getAvailableInventory(variant, root);
  if (available !== null) {
    if (available <= 0) {
      return 0;
    }
    max = Math.min(max, available);
  }

  const minRule = quantityRule?.min != null ? Number(quantityRule.min) : 1;
  if (Number.isFinite(minRule) && minRule >= 1 && max > 0 && minRule > max) {
    return 0;
  }

  return Math.max(0, max);
}

/**
 * @param {HTMLElement} root
 * @returns {number}
 */
function getSelectedTierQty(root) {
  const checked = root.querySelector('[data-qubo-camera-qty-option]:checked');
  if (checked instanceof HTMLInputElement) {
    return Number(checked.value) || 1;
  }
  return 1;
}

/**
 * @param {HTMLElement} root
 * @param {number} maxTier
 * @returns {number[]}
 */
function getEligibleTiers(root, maxTier) {
  const tiers = [];

  root.querySelectorAll('[data-tier-qty]').forEach((label) => {
    if (!(label instanceof HTMLElement)) return;

    const tier = Number(label.dataset.tierQty) || 0;
    if (tier < 1 || tier > maxTier) return;
    if (label.hidden || label.classList.contains('qubo-camera-quantity__option--hidden')) return;

    tiers.push(tier);
  });

  return tiers.sort((a, b) => a - b);
}

/**
 * @param {HTMLElement} root
 * @param {number} maxTier
 * @returns {number}
 */
function clampSelectedTier(root, maxTier) {
  const eligible = getEligibleTiers(root, maxTier);
  if (eligible.length === 0) return 1;

  const current = getSelectedTierQty(root);
  let next = current;

  if (!eligible.includes(current)) {
    const atOrBelow = eligible.filter((tier) => tier <= current);
    next = atOrBelow.length > 0 ? Math.max(...atOrBelow) : eligible[0];
  }

  root.querySelectorAll('[data-qubo-camera-qty-option]').forEach((input) => {
    if (!(input instanceof HTMLInputElement) || input.disabled) return;
    const tier = Number(input.value) || 0;
    input.checked = tier === next;
  });

  return next;
}

/**
 * @param {HTMLElement} saveEl
 * @param {number} saveCents
 */
function updateSaveBadge(saveEl, saveCents) {
  if (saveCents > 0) {
    saveEl.textContent = `Save ${formatMoney(saveCents)}`;
    saveEl.classList.remove('qubo-camera-quantity__save--hidden');
  } else {
    saveEl.textContent = '';
    saveEl.classList.add('qubo-camera-quantity__save--hidden');
  }
}

/**
 * @param {HTMLElement} root
 * @param {number} unitPriceCents
 * @param {number} compareAtCents
 * @param {number} maxTier
 */
function updateTierPrices(root, unitPriceCents, compareAtCents, maxTier = UI_TIER_CAP) {
  if (!Number.isFinite(unitPriceCents) || unitPriceCents <= 0) return;

  root.setAttribute('data-unit-price', String(unitPriceCents));
  root.setAttribute('data-compare-at-price', String(Number.isFinite(compareAtCents) ? compareAtCents : 0));

  const unitSave = getUnitSaveCents(unitPriceCents, compareAtCents);

  root.querySelectorAll('[data-tier-display-price]').forEach((priceEl) => {
    const qty = Number(priceEl.getAttribute('data-qty')) || 0;
    if (qty < 1) return;

    priceEl.textContent = formatMoney(unitPriceCents * qty);
  });

  root.querySelectorAll('[data-tier-save-badge]').forEach((saveEl) => {
    const qty = Number(saveEl.getAttribute('data-qty')) || 0;
    if (qty < 1) return;

    updateSaveBadge(saveEl, unitSave * qty);
  });
}

/**
 * @param {HTMLElement} wrapper
 * @param {number} qty
 * @param {number} maxTier
 * @param {Record<string, unknown> | null | undefined} variant
 */
function syncQuantityInput(wrapper, qty, maxTier, variant) {
  const input = wrapper.querySelector('[data-qubo-camera-quantity-input]');
  if (!(input instanceof HTMLInputElement)) return;

  const quantityRule = /** @type {{ min?: number; increment?: number }} | undefined */ (variant?.quantity_rule);
  const min = quantityRule?.min != null ? String(quantityRule.min) : '1';
  const step =
    quantityRule?.increment != null && quantityRule.increment > 0
      ? String(quantityRule.increment)
      : '1';

  input.min = min;
  input.step = step;

  if (maxTier >= 1) {
    input.max = String(maxTier);
    input.value = String(qty);
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));

  const selector = wrapper.querySelector('qubo-camera-quantity-selector-component');
  if (selector && typeof selector.updateConstraints === 'function') {
    selector.updateConstraints(min, maxTier >= 1 ? String(maxTier) : null, step);
  }
}

/**
 * @param {HTMLElement} root
 * @param {Record<string, unknown> | null | undefined} variant
 */
function syncInventoryAttributes(root, variant) {
  const { managed, inventoryQty } = getInventorySnapshot(root, variant);

  if (managed) {
    root.setAttribute('data-inventory-managed', 'true');
  }

  const policy = resolveInventoryPolicy(variant, root);
  if (policy === 'continue') {
    root.setAttribute('data-inventory-policy', 'continue');
    root.setAttribute('data-inventory-continue', 'true');
  } else if (policy === 'deny') {
    root.setAttribute('data-inventory-policy', 'deny');
    root.setAttribute('data-inventory-continue', 'false');
  }

  if (Number.isFinite(inventoryQty)) {
    root.setAttribute('data-inventory-quantity', String(inventoryQty));
  }

  if (variant?.id != null) {
    root.setAttribute('data-variant-id', String(variant.id));
  }
}

/**
 * @param {HTMLElement} root
 * @param {Record<string, unknown> | null | undefined} variant
 */
function applyTierLimits(root, variant) {
  const wrapper = root.closest('.qubo-camera-quantity-wrapper');
  if (!wrapper) return;

  const pickerVariant = variant ?? getVariantFromPicker(
    root.closest('[data-how-many-cameras-block]')?.dataset.productId
  );

  syncInventoryAttributes(root, pickerVariant);

  let maxTier = calculateMaxTier(pickerVariant, root);

  const liquidMax = parseInt(root.getAttribute('data-liquid-max-tier') || '', 10);
  const liquidVariantId = root.getAttribute('data-liquid-variant-id');
  const activeVariantId =
    pickerVariant?.id != null ? String(pickerVariant.id) : root.getAttribute('data-variant-id') || '';
  const variantMatches =
    !liquidVariantId || !activeVariantId || liquidVariantId === activeVariantId;

  if (maxTier < 1 && Number.isFinite(liquidMax) && liquidMax > 0 && variantMatches) {
    maxTier = liquidMax;
    const available = getAvailableInventory(pickerVariant, root);
    if (available !== null) {
      maxTier = available <= 0 ? 0 : Math.min(maxTier, available);
    }
  }

  root.setAttribute('data-max-tier', String(maxTier));

  const selector = wrapper.querySelector('qubo-camera-quantity-selector-component');
  if (selector instanceof HTMLElement) {
    const quantityRule = /** @type {{ max?: number | null }} | undefined */ (pickerVariant?.quantity_rule);
    if (quantityRule?.max != null && Number.isFinite(quantityRule.max)) {
      selector.dataset.variantQtyMax = String(quantityRule.max);
    } else {
      delete selector.dataset.variantQtyMax;
    }
  }

  root.querySelectorAll('[data-tier-qty]').forEach((label) => {
    if (!(label instanceof HTMLElement)) return;
    const tier = Number(label.dataset.tierQty) || 0;
    const hideTier = maxTier < 1 || tier > maxTier;
    label.toggleAttribute('hidden', hideTier);
    label.classList.toggle('qubo-camera-quantity__option--hidden', hideTier);

    const input = label.querySelector('[data-qubo-camera-qty-option]');
    if (input instanceof HTMLInputElement) {
      input.disabled = hideTier;
    }
  });

  const hasVisibleTier = getEligibleTiers(root, maxTier).length > 0;
  const hideWrapper = maxTier < 1 || (usesMetafieldTiers(root) && !hasVisibleTier);
  wrapper.toggleAttribute('hidden', hideWrapper);
  wrapper.classList.toggle('qubo-camera-quantity-wrapper--hidden', hideWrapper);

  const block = root.closest('[data-how-many-cameras-block]');
  if (block instanceof HTMLElement) {
    block.toggleAttribute('hidden', hideWrapper);
    block.classList.toggle('how-many-cameras-block--hidden', hideWrapper);
  }

  const aiGuardNote = wrapper.querySelector('[data-ai-guard-note]');
  if (aiGuardNote instanceof HTMLElement) {
    aiGuardNote.toggleAttribute('hidden', hideWrapper);
    aiGuardNote.classList.toggle('how-many-cameras-block__ai-guard-note--hidden', hideWrapper);
  }

  if (hideWrapper) return;

  block?.classList.remove('how-many-cameras-block--hidden');
  block?.removeAttribute('hidden');

  const selectedQty = clampSelectedTier(root, maxTier);
  syncQuantityInput(wrapper, selectedQty, maxTier, pickerVariant);
  requestAnimationFrame(() => updateCameraQtyOverflowState(root));

  const price = Number(pickerVariant?.price);
  const compareAt = Number(pickerVariant?.compare_at_price);
  if (Number.isFinite(price) && price > 0) {
    updateTierPrices(root, price, compareAt, maxTier);
  } else {
    const domPrice = Number(root.getAttribute('data-unit-price'));
    const domCompare = Number(root.getAttribute('data-compare-at-price'));
    if (Number.isFinite(domPrice) && domPrice > 0) {
      updateTierPrices(root, domPrice, domCompare, maxTier);
    }
  }
}

/**
 * @param {string | undefined} productId
 * @returns {Record<string, unknown> | null}
 */
function getVariantFromPicker(productId) {
  const picker =
    (productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelector('variant-picker')) ?? null;

  const script = picker?.querySelector('script[type="application/json"]');
  if (!script?.textContent) return null;

  try {
    return /** @type {Record<string, unknown>} */ (JSON.parse(script.textContent));
  } catch {
    return null;
  }
}

/** @type {WeakMap<HTMLElement, number>} */
const cameraQtyScrollStep = new WeakMap();

/** @type {WeakMap<HTMLElement, boolean>} */
const cameraQtyScrollAnimating = new WeakMap();

/**
 * @param {HTMLElement} row
 * @returns {HTMLElement[]}
 */
function getVisibleCameraQtyOptions(row) {
  return [...row.querySelectorAll(
    '.qubo-camera-quantity__option:not(.qubo-camera-quantity__option--hidden):not([hidden])'
  )].filter((option) => option instanceof HTMLElement);
}

/**
 * @param {HTMLElement} row
 * @returns {number[]}
 */
function getCameraQtySnapPositions(row) {
  const maxScrollLeft = Math.max(0, Math.round(row.scrollWidth - row.clientWidth));
  /** @type {number[]} */
  const positions = [0];

  getVisibleCameraQtyOptions(row).forEach((option) => {
    const left = Math.round(option.offsetLeft);
    if (left > 0 && left < maxScrollLeft) {
      positions.push(left);
    }
  });

  if (maxScrollLeft > 0 && positions[positions.length - 1] !== maxScrollLeft) {
    positions.push(maxScrollLeft);
  }

  return [...new Set(positions)].sort((a, b) => a - b);
}

/**
 * @param {HTMLElement} row
 * @param {number[]} positions
 * @returns {number}
 */
function getCameraQtyScrollStep(row, positions) {
  const scrollLeft = Math.round(row.scrollLeft);

  if (scrollLeft <= 0 || positions.length <= 1) {
    return 0;
  }

  const maxScrollLeft = positions[positions.length - 1];
  if (scrollLeft >= maxScrollLeft) {
    return positions.length - 1;
  }

  let nearestStep = 0;
  let nearestDistance = Infinity;

  positions.forEach((position, index) => {
    const distance = Math.abs(scrollLeft - position);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestStep = index;
    }
  });

  return nearestStep;
}

/**
 * @param {HTMLElement} row
 * @param {number} step
 * @param {number[]} positions
 * @param {{ smooth?: boolean }} [options]
 */
function setCameraQtyScrollStep(row, step, positions, options = {}) {
  if (!positions.length) {
    return;
  }

  const { smooth = false } = options;
  const nextStep = Math.min(Math.max(step, 0), positions.length - 1);
  const targetLeft = positions[nextStep];

  cameraQtyScrollStep.set(row, nextStep);

  if (!smooth) {
    row.scrollLeft = targetLeft;
    return;
  }

  cameraQtyScrollAnimating.set(row, true);
  row.scrollTo({ left: targetLeft, behavior: 'smooth' });

  const finishSmoothScroll = () => {
    if (!cameraQtyScrollAnimating.get(row)) {
      return;
    }

    cameraQtyScrollAnimating.delete(row);
    clampCameraQtyScrollEdges(row);

    const root = row.closest('[data-qubo-camera-quantity]');
    if (root instanceof HTMLElement) {
      updateCameraQtyOverflowState(root);
    }
  };

  row.addEventListener('scrollend', finishSmoothScroll, { once: true });
  window.setTimeout(finishSmoothScroll, 450);
}

/**
 * @param {HTMLElement} row
 * @param {number} direction
 */
function scrollCameraQtyRow(row, direction) {
  const positions = getCameraQtySnapPositions(row);
  if (positions.length <= 1) {
    return;
  }

  const currentStep = cameraQtyScrollStep.get(row) ?? getCameraQtyScrollStep(row, positions);
  const nextStep =
    direction < 0
      ? Math.max(0, currentStep - 1)
      : Math.min(positions.length - 1, currentStep + 1);

  setCameraQtyScrollStep(row, nextStep, positions, { smooth: true });
}

/**
 * @param {HTMLElement} row
 */
function snapCameraQtyRowAfterGesture(row) {
  const positions = getCameraQtySnapPositions(row);
  if (!positions.length) {
    return;
  }

  setCameraQtyScrollStep(row, getCameraQtyScrollStep(row, positions), positions);
}

/**
 * @param {HTMLElement} row
 */
function clampCameraQtyScrollEdges(row) {
  const maxScrollLeft = Math.max(0, Math.round(row.scrollWidth - row.clientWidth));

  if (row.scrollLeft <= 4) {
    row.scrollLeft = 0;
    cameraQtyScrollStep.set(row, 0);
    return;
  }

  if (row.scrollLeft >= maxScrollLeft - 4) {
    row.scrollLeft = maxScrollLeft;
    const positions = getCameraQtySnapPositions(row);
    if (positions.length) {
      cameraQtyScrollStep.set(row, positions.length - 1);
    }
  }
}

/**
 * @param {HTMLElement} root
 */
function updateCameraQtyOverflowState(root) {
  const overflow = root.querySelector('[data-camera-qty-overflow]');
  const row = root.querySelector('[data-camera-qty-options-row]');
  const prevButton = root.querySelector('[data-camera-qty-scroll-prev]');
  const nextButton = root.querySelector('[data-camera-qty-scroll-next]');

  if (
    !(overflow instanceof HTMLElement) ||
    !(row instanceof HTMLElement) ||
    !(prevButton instanceof HTMLButtonElement) ||
    !(nextButton instanceof HTMLButtonElement)
  ) {
    return;
  }

  if (!cameraQtyScrollAnimating.get(row)) {
    clampCameraQtyScrollEdges(row);
  }

  const maxScrollLeft = Math.max(row.scrollWidth - row.clientWidth, 0);
  const hasOverflow = maxScrollLeft > 4;
  const disablePrevButton = !hasOverflow || row.scrollLeft <= 4;
  const disableNextButton = !hasOverflow || row.scrollLeft >= maxScrollLeft - 4;

  overflow.classList.toggle('is-scrollable', hasOverflow);
  prevButton.disabled = disablePrevButton;
  nextButton.disabled = disableNextButton;
}

/**
 * @param {HTMLElement} root
 */
function bindCameraQtyOverflow(root) {
  if (root.dataset.quboCameraQtyOverflowBound === 'true') {
    updateCameraQtyOverflowState(root);
    return;
  }
  root.dataset.quboCameraQtyOverflowBound = 'true';

  root.addEventListener('click', (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const button = event.target.closest('[data-camera-qty-scroll-prev], [data-camera-qty-scroll-next]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const row = root.querySelector('[data-camera-qty-options-row]');
    if (!(row instanceof HTMLElement)) return;

    event.preventDefault();

    const direction = button.matches('[data-camera-qty-scroll-next]') ? 1 : -1;
    scrollCameraQtyRow(row, direction);
    updateCameraQtyOverflowState(root);
  });

  const row = root.querySelector('[data-camera-qty-options-row]');
  if (row instanceof HTMLElement) {
    let gestureSnapTimer = 0;
    row.addEventListener(
      'scroll',
      () => {
        updateCameraQtyOverflowState(root);

        if (gestureSnapTimer) {
          window.clearTimeout(gestureSnapTimer);
        }
        gestureSnapTimer = window.setTimeout(() => {
          gestureSnapTimer = 0;
          if (cameraQtyScrollAnimating.get(row)) {
            return;
          }
          snapCameraQtyRowAfterGesture(row);
          clampCameraQtyScrollEdges(row);
          updateCameraQtyOverflowState(root);
        }, 150);
      },
      { passive: true }
    );
  }

  const handleResize = () => {
    if (row instanceof HTMLElement) {
      clampCameraQtyScrollEdges(row);
    }
    updateCameraQtyOverflowState(root);
  };
  window.addEventListener('resize', handleResize);
  CAMERA_QTY_MOBILE_MQ.addEventListener('change', handleResize);
  requestAnimationFrame(() => updateCameraQtyOverflowState(root));
}

/**
 * @param {HTMLElement} root
 */
function bindCameraQuantity(root) {
  if (root.dataset.quboCameraQuantityBound === 'true') return;
  root.dataset.quboCameraQuantityBound = 'true';

  const wrapper = root.closest('.qubo-camera-quantity-wrapper');
  if (!wrapper) return;

  bindCameraQtyOverflow(root);

  const section = root.closest('.shopify-section');
  const productId =
    root.closest('[data-how-many-cameras-block]')?.dataset.productId ||
    section?.querySelector('product-form-component[data-product-id]')?.dataset.productId;
  const variant = getVariantFromPicker(productId);
  applyTierLimits(root, variant);

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-qubo-camera-qty-option]')) {
      return;
    }

    const maxTier = Number(root.getAttribute('data-max-tier')) || UI_TIER_CAP;
    const qty = Math.min(Number(target.value) || 1, maxTier);
    const blockEl = root.closest('[data-how-many-cameras-block]');
    const pickerVariant = getVariantFromPicker(blockEl?.dataset.productId);
    syncQuantityInput(wrapper, qty, maxTier, pickerVariant);
  });

  const cameraSelector = wrapper.querySelector('qubo-camera-quantity-selector-component');
  if (cameraSelector) {
    cameraSelector.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
      if (event.detail?.cartLine) return;

      const maxTier = Number(root.getAttribute('data-max-tier')) || UI_TIER_CAP;
      const qty = Math.min(Math.max(Number(event.detail?.quantity) || 1, 1), maxTier);

      root.querySelectorAll('[data-qubo-camera-qty-option]').forEach((input) => {
        if (!(input instanceof HTMLInputElement) || input.disabled) return;
        input.checked = Number(input.value) === qty;
      });
    });
  }
}

function initAll() {
  document.querySelectorAll('[data-qubo-camera-quantity]').forEach((el) => {
    if (el instanceof HTMLElement) {
      bindCameraQuantity(el);
    }
  });
}

/**
 * @param {CustomEvent} event
 */
function handleVariantUpdate(event) {
  const variant = /** @type {Record<string, unknown> | null | undefined} */ (event.detail?.resource);
  const eventProductId = event.detail?.data?.productId;
  if (!eventProductId) return;

  document.querySelectorAll('[data-qubo-camera-quantity]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;

    const block = root.closest('[data-how-many-cameras-block]');
    const blockProductId = block?.dataset.productId;
    if (blockProductId && blockProductId !== String(eventProductId)) return;

    bindCameraQuantity(root);
    applyTierLimits(root, variant ?? null);
  });
}

document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);

document.addEventListener(ThemeEvents.cartUpdate, () => {
  document.querySelectorAll('[data-qubo-camera-quantity]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;

    const block = root.closest('[data-how-many-cameras-block]');
    const productId = block?.dataset.productId;
    applyTierLimits(root, getVariantFromPicker(productId));
  });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);
